import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/client/lib/api';
import { letterMonthLabel, letterPath, type PublicLetterSummary } from '@/lib/letters';
import { useMeta } from '@/client/lib/useMeta';
import { FramedImage } from '@/components/FramedImage';
import { BRAND } from '@/config/site';
import { Newsletter } from '@/features/newsletter/Newsletter';
import { useI18n } from '@/client/i18n/I18nContext';
import { staticPageText } from '@/lib/seo';
import { lp } from '@/client/i18n/locale';

// Dedicated /newsletter page: Carolina's invitation to the letter. The signup
// form is the shared double opt-in `Newsletter` band. Unsubscribing is token-
// based via each letter's `/newsletter/baja` link; the mailto is the manual fallback.
// Below the form, the archive of the letters already sent (docs/plan-cartas.md;
// hidden while there is none or the read fails).
export function NewsletterPage() {
  const { t, locale } = useI18n();
  useMeta({ ...staticPageText('/newsletter', locale), path: '/newsletter' });
  const [letters, setLetters] = useState<PublicLetterSummary[]>([]);
  useEffect(() => {
    let alive = true;
    api
      .letters()
      .then((list) => alive && setLetters(list))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return (
    <div className="container">
      <div className="pdp" style={{ paddingBlock: 'var(--space-6)', alignItems: 'start' }}>
        <div className="casa-card__media" style={{ aspectRatio: '4 / 5' }}>
          <FramedImage src="/media/caro/la-carta-de-caro-newsletter-carolina-collaer.webp" alt={t('newsletterPage.imageAlt')} fallback="CARO" />
        </div>
        <div className="stack">
          <p className="eyebrow">{t('newsletterPage.eyebrow')}</p>
          <h1>{t('newsletterPage.title')}</h1>
          <p className="lede">{t('newsletterPage.lede')}</p>
          <p>{t('newsletterPage.p1')}</p>
          <p>{t('newsletterPage.p2')}</p>
          <p>
            {t('newsletterPage.signoff')}
            <br />
            <span style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 'var(--fs-lg)' }}>
              {t('newsletterPage.signature')}
            </span>
          </p>
        </div>
      </div>

      <Newsletter title={t('newsletterPage.formTitle')} body={t('newsletterPage.formBody')} />

      {letters.length > 0 ? (
        <section className="section" aria-labelledby="nl-archive">
          <h2 id="nl-archive" style={{ fontSize: 'var(--fs-xl)' }}>{t('newsletterPage.archiveTitle')}</h2>
          <ul className="stack" style={{ listStyle: 'none', padding: 0 }}>
            {letters.map((l) => (
              <li key={l.month}>
                <p className="eyebrow">{letterMonthLabel(l.month, locale)}</p>
                <Link to={lp(letterPath(l, locale))} className="link-underline">{l.content[locale].title}</Link>
                <p className="text-muted">{l.content[locale].summary}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="section" aria-labelledby="nl-unsub" style={{ textAlign: 'center' }}>
        <h2 id="nl-unsub" style={{ fontSize: 'var(--fs-xl)' }}>{t('newsletterPage.unsubTitle')}</h2>
        <p className="text-muted" style={{ maxWidth: '60ch', marginInline: 'auto' }}>
          {t('newsletterPage.unsubBody')}
        </p>
        <p>
          <a
            className="link-underline"
            href={`mailto:${BRAND.contactEmail}?subject=${encodeURIComponent(t('newsletterPage.unsubSubject'))}`}
          >
            {t('newsletterPage.unsubLink')}
          </a>
        </p>
        <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
          {t('newsletterPage.privacyLead')}{' '}
          <Link to={lp('/legal/privacidad')} className="link-underline">{t('newsletterPage.privacyLink')}</Link>.
        </p>
      </section>
    </div>
  );
}
